"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, Sparkles, MapPin, Info } from "lucide-react";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const kitchenProjects = [{
  id: "historic-style-kitchen",
  title: "Historic-Style Kitchen Concept",
  neighborhood: "Brookhaven, GA",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/historic_brookhaven_kitchen.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/historic_brookhaven_kitchen.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/historic_brookhaven_kitchen.png-wvc-sizes",
  alt: "Historic-style kitchen design concept with white oak cabinetry and quartzite island",
  cabinetry: "Custom inset white oak",
  countertop: "Waterfall honed quartzite",
  accents: "Unlacquered brass hardware"
}, {
  id: "contemporary-navy-kitchen",
  title: "Contemporary Navy Kitchen Concept",
  neighborhood: "Brookhaven, GA",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/capital_city_club_kitchen.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/capital_city_club_kitchen.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/capital_city_club_kitchen.png-wvc-sizes",
  alt: "Contemporary navy kitchen design concept with fluted island and slab backsplash",
  cabinetry: "Deep navy fluted island & painted millwork",
  countertop: "Calacatta slab porcelain",
  accents: "Integrated panel-ready refrigeration"
}, {
  id: "modern-architectural-kitchen",
  title: "Modern Architectural Kitchen Concept",
  neighborhood: "Brookhaven, GA",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/peachtree_dunwoody_kitchen.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/peachtree_dunwoody_kitchen.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/peachtree_dunwoody_kitchen.png-wvc-sizes",
  alt: "Modern architectural kitchen design concept with reeded glass upper cabinets and custom hood",
  cabinetry: "Full-height reeded glass uppers",
  countertop: "Perimeter composite quartz",
  accents: "Bespoke range hood with brushed gold strapping"
}, {
  id: "transitional-kitchen",
  title: "Transitional Kitchen Concept",
  neighborhood: "Brookhaven, GA",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/ashford_park_kitchen.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/ashford_park_kitchen.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/ashford_park_kitchen.png-wvc-sizes",
  alt: "Transitional kitchen design concept with zellige tile and Taj Mahal quartzite",
  cabinetry: "Shaker profile in warm greige",
  countertop: "Honed Taj Mahal quartzite",
  accents: "Handcrafted zellige tile & twin preparation sinks"
}];
export default function Gallery() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 15
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0.01 : 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="kitchen-gallery" data-nav="light" className="relative bg-background py-24 md:py-32">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-9 lg:px-16">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: shouldReduceMotion ? 0.01 : 0.5,
        ease: [0.16, 1, 0.3, 1]
      }} className="mx-auto mb-12 max-w-2xl text-center md:mb-16">
          <div className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-3.5 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-primary">
            <Sparkles className="size-3.5 text-primary" />
            <span>Project gallery</span>
          </div>

          <h2 className="font-serif text-3xl font-semibold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Custom kitchens tailored to Brookhaven homes.
          </h2>

          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            Explore recent renovations showcasing artisan millwork, natural stone, and purposeful architectural layouts.
          </p>

          <div className="mx-auto mt-6 h-px w-24 bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
        </motion.div>

        {/* Visible Inspiration & Design Concept Notice */}
        <div className="mx-auto mb-10 flex max-w-3xl items-center justify-center gap-2.5 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 text-center sm:px-6">
          <Info className="size-4 shrink-0 text-primary" />
          <p className="text-xs font-medium text-foreground sm:text-sm">
            These images are Remodeling Inspiration &amp; Design Concepts — they are not photographs of completed Brookhaven Elevation Renovations projects.
          </p>
        </div>

        {/* Gallery Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:gap-10">
          {kitchenProjects.map((project, i) => <motion.article key={project.id} data-index={i} variants={cardVariants} whileHover={shouldReduceMotion ? undefined : {
          y: -4,
          transition: {
            duration: 0.25,
            ease: [0.2, 0.8, 0.2, 1]
          }
        }} className="group flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-xs transition-[border-color,box-shadow] duration-300 hover:border-primary/50 hover:shadow-lg">
              {/* Image Container with 4:3 Aspect Ratio */}
              <div className="relative overflow-hidden bg-muted">
                <AspectRatio ratio={4 / 3}>
                  <img src={project.image} data-wvc-srcset={project.imageSrcset} data-wvc-sizes={project.imageSizes} alt={project.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105" />
                </AspectRatio>
                {/* Subtle Inner Ring Overlay */}
                <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-foreground/10 rounded-t-xl" />

                {/* General Location Badge */}
                <div className="absolute top-4 left-4">
                  <Badge variant="secondary" className="flex items-center gap-1.5 rounded-full border border-border/80 bg-background/90 px-3 py-1 text-xs font-medium text-foreground backdrop-blur-md shadow-2xs">
                    <MapPin className="size-3 text-primary" />
                    <span>{project.neighborhood}</span>
                  </Badge>
                </div>
              </div>

              {/* Lower Metadata Panel */}
              <div className="flex flex-1 flex-col justify-between p-6 sm:p-7">
                <div>
                  <h3 className="font-serif text-2xl font-semibold tracking-tight text-foreground">
                    {project.title}
                  </h3>

                  <div className="mt-5 space-y-2.5 border-t border-border/60 pt-4 text-sm">
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="font-medium text-muted-foreground">
                        Cabinetry
                      </span>
                      <span className="text-right text-foreground font-normal">
                        {project.cabinetry}
                      </span>
                    </div>

                    <div className="flex items-baseline justify-between gap-3">
                      <span className="font-medium text-muted-foreground">
                        Surface
                      </span>
                      <span className="text-right text-foreground font-normal">
                        {project.countertop}
                      </span>
                    </div>

                    <div className="flex items-baseline justify-between gap-3">
                      <span className="font-medium text-muted-foreground">
                        Finish Details
                      </span>
                      <span className="text-right text-foreground font-normal">
                        {project.accents}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.article>)}
        </motion.div>

        {/* Section Action Call-to-action */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.5
      }} transition={{
        duration: shouldReduceMotion ? 0.01 : 0.5,
        ease: [0.16, 1, 0.3, 1]
      }} className="mt-16 flex flex-col items-center justify-center gap-4 text-center md:mt-20">
          <Button asChild variant="secondary" className="group h-12 rounded-lg px-8 text-base font-medium shadow-sm transition-[background-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:bg-secondary/90 hover:shadow-md">
            <Link to="/OurProjectsPage" className="inline-flex items-center gap-2">
              <span>View all completed projects</span>
              <ArrowUpRight className="size-4 text-primary transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Button>

          <p className="text-xs text-muted-foreground">
            Serving Historic Brookhaven, Capital City Club, Peachtree Dunwoody, and Ashford Park
          </p>
        </motion.div>
      </div>
    </section>;
}